import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { IDS } from '@/constants/testIds';
import { useAuth, useGuide } from '@/context/AuthContext';
import { useBuddyVoice } from '@/lib/voice';
import { createRecorder } from '@/lib/recorder';
import CoreyAvatar from '@/components/CoreyAvatar';
import GuidePickerModal from '@/components/GuidePickerModal';
import ModeSwitcher from '@/components/ModeSwitcher';
import { Mic, StopCircle, Camera, X, Loader2, Wrench, AlertTriangle, MessageCircleQuestion, Volume2 } from 'lucide-react';

const TRADES = [
  'Auto-detect', 'Plumbing', 'Electrical', 'HVAC', 'Carpentry', 'Roofing',
  'Appliance Repair', 'Painting', 'Flooring', 'Masonry', 'Drywall', 'Landscaping', 'General Handyman',
];

async function fileToBase64(file) {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function AskPage() {
  const [description, setDescription] = useState('');
  const [trade, setTrade] = useState('Auto-detect');
  const [photoDataUrl, setPhotoDataUrl] = useState(null);
  const [photoMime, setPhotoMime] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const guide = useGuide();
  const [showPicker, setShowPicker] = useState(false);
  const [autoBuddy, setAutoBuddy] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { playBase64, speakText, stop: stopVoice } = useBuddyVoice();

  // Clarification state
  const [clarifyQuestion, setClarifyQuestion] = useState('');
  const [clarifyAudio, setClarifyAudio] = useState('');
  const [clarifyReply, setClarifyReply] = useState('');
  const [replyRecording, setReplyRecording] = useState(false);
  const [replyTranscribing, setReplyTranscribing] = useState(false);
  const replyRecorderRef = useRef(null);
  const replyChunksRef = useRef([]);
  const replyStreamRef = useRef(null);

  // Auto-open picker if this is the user's first visit (no guide set yet)
  // OR if they came in with a pre-selected buddy via ?autoBuddy=corey|christine
  useEffect(() => {
    if (!user) return;
    const auto = searchParams.get('autoBuddy');
    if (auto === 'corey' || auto === 'christine') {
      setAutoBuddy(auto);
      setShowPicker(true);
      // Clear the URL param so refresh doesn't re-trigger auto-pick
      const next = new URLSearchParams(searchParams);
      next.delete('autoBuddy');
      setSearchParams(next, { replace: true });
      return;
    }
    if (!user.guide) setShowPicker(true);
  }, [user, searchParams, setSearchParams]);

  // Stop any audio when leaving
  useEffect(() => () => stopVoice(), [stopVoice]);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpeg|jpg|webp)$/i.test(file.type)) {
      setError('Photo must be PNG, JPEG, or WEBP');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('Photo must be under 8MB');
      return;
    }
    setError('');
    const dataUrl = await fileToBase64(file);
    setPhotoDataUrl(dataUrl);
    setPhotoMime(file.type);
  };

  const removePhoto = () => {
    setPhotoDataUrl(null);
    setPhotoMime(null);
  };

  const startRecording = async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const { recorder: mr, mimeInfo } = createRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (ev) => {
        if (ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      mr.onstop = async () => {
        setRecording(false);
        setTranscribing(true);
        setVoiceStatus('Transcribing…');
        try {
          const blobType = mimeInfo.type || (mr.mimeType || 'audio/webm');
          const blob = new Blob(chunksRef.current, { type: blobType });
          const form = new FormData();
          form.append('audio', blob, `recording.${mimeInfo.ext}`);
          const res = await api.post('/voice/transcribe', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const text = (res.data?.text || '').trim();
          if (text) {
            setDescription((prev) => (prev ? `${prev} ${text}` : text));
            setVoiceStatus('Added to description ✓');
          } else {
            setVoiceStatus('No speech detected');
          }
        } catch (err) {
          setError(err.response?.data?.detail || 'Voice transcription failed');
          setVoiceStatus('');
        } finally {
          setTranscribing(false);
          setTimeout(() => setVoiceStatus(''), 3000);
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
          }
        }
      };
      mr.start();
      setRecording(true);
      setVoiceStatus('Recording… tap again to stop');
    } catch (err) {
      const name = err?.name || '';
      let msg = 'Microphone unavailable. Try Chrome or Safari and allow mic access.';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        msg = 'Mic permission denied. Tap the lock icon in your address bar to allow it.';
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        msg = 'No microphone found on this device.';
      } else if (err?.message) {
        msg = `Mic error: ${err.message}`;
      }
      setError(msg);
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
  };

  const submitProblem = async (clarification = null) => {
    setSubmitting(true);
    try {
      const payload = {
        description: description.trim(),
        trade: trade === 'Auto-detect' ? null : trade,
        image_base64: photoDataUrl || null,
        image_mime: photoMime || null,
        clarification: clarification || null,
      };
      const res = await api.post('/problems', payload);
      navigate(`/solution/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to get solution. Please try again.');
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setError('');
    if (description.trim().length < 3) {
      setError('Please describe your problem (at least a few words).');
      return;
    }
    setSubmitting(true);
    try {
      // Step 1: ask the buddy if we need a clarifying question first
      const res = await api.post('/problems/clarify', {
        description: description.trim(),
        trade: trade === 'Auto-detect' ? null : trade,
        has_image: !!photoDataUrl,
      });
      if (res.data?.needs_clarification && res.data?.question) {
        setClarifyQuestion(res.data.question);
        setClarifyAudio(res.data.audio_base64 || '');
        setClarifyReply('');
        setSubmitting(false);
        // Auto-play the spoken question
        if (res.data.audio_base64) {
          playBase64(res.data.audio_base64);
        }
        return;
      }
      // No clarification needed → solve directly
      await submitProblem(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reach the AI. Please try again.');
      setSubmitting(false);
    }
  };

  const skipClarification = async () => {
    setClarifyQuestion('');
    setClarifyAudio('');
    stopVoice();
    await submitProblem(null);
  };

  const submitClarification = async () => {
    const reply = clarifyReply.trim();
    if (!reply) {
      setError('Give a quick answer, or hit "Skip" to just try it.');
      return;
    }
    setError('');
    setClarifyQuestion('');
    setClarifyAudio('');
    stopVoice();
    await submitProblem(reply);
  };

  const replayClarifyQuestion = () => {
    if (clarifyAudio) playBase64(clarifyAudio);
    else if (clarifyQuestion) speakText(clarifyQuestion);
  };

  // ---- Voice reply to buddy's clarifying question ----
  const startReplyRecording = async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      replyStreamRef.current = stream;
      const { recorder: mr, mimeInfo } = createRecorder(stream);
      replyRecorderRef.current = mr;
      replyChunksRef.current = [];
      mr.ondataavailable = (ev) => { if (ev.data.size > 0) replyChunksRef.current.push(ev.data); };
      mr.onstop = async () => {
        setReplyRecording(false);
        setReplyTranscribing(true);
        try {
          const blobType = mimeInfo.type || (mr.mimeType || 'audio/webm');
          const blob = new Blob(replyChunksRef.current, { type: blobType });
          const form = new FormData();
          form.append('audio', blob, `reply.${mimeInfo.ext}`);
          const res = await api.post('/voice/transcribe', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const text = (res.data?.text || '').trim();
          if (text) setClarifyReply((prev) => (prev ? `${prev} ${text}` : text));
        } catch {
          setError('Voice reply transcription failed');
        } finally {
          setReplyTranscribing(false);
          if (replyStreamRef.current) {
            replyStreamRef.current.getTracks().forEach((t) => t.stop());
            replyStreamRef.current = null;
          }
        }
      };
      mr.start();
      setReplyRecording(true);
    } catch (err) {
      const name = err?.name || '';
      let msg = 'Microphone unavailable';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        msg = 'Mic permission denied — tap the lock icon in your address bar to allow it.';
      } else if (name === 'NotFoundError') {
        msg = 'No microphone found on this device';
      }
      setError(msg);
    }
  };

  const stopReplyRecording = () => replyRecorderRef.current?.stop();

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <div className="mb-8 fade-up flex items-start gap-4">
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          className="hidden sm:block group relative"
          title="Change your DIY buddy"
        >
          <CoreyAvatar size="lg" />
          <div className="absolute inset-x-0 -bottom-2 text-center opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="inline-block bg-zinc-900 border border-zinc-700 font-mono text-[9px] uppercase tracking-widest text-amber-400 px-2 py-0.5">
              Change
            </span>
          </div>
        </button>
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-lime-400 mb-2">
            /new_problem
          </div>
          <h1 className="font-display text-5xl sm:text-6xl text-zinc-100 tracking-tight leading-none">
            WHAT'S <span className="text-gradient-hot">BROKEN?</span>
          </h1>
          <p className="text-zinc-400 mt-4 text-lg">
            <span className="font-handwritten text-2xl text-amber-400">
              {guide.name} here —
            </span>{' '}
            type it, talk it, or snap it. {guide.pronoun_cap}'ll walk you through the fix.
          </p>
          <div className="mt-4">
            <ModeSwitcher variant="compact" />
          </div>
        </div>
      </div>

      {showPicker && (
        <GuidePickerModal
          onClose={() => { setShowPicker(false); setAutoBuddy(null); }}
          dismissible={!!user?.guide}
          autoPick={autoBuddy}
        />
      )}

      <div className="fade-up delay-100 industrial-card border-t-4 border-t-amber-400 space-y-6">
        {/* Trade Selector */}
        <div>
          <label className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2 block">
            Trade
          </label>
          <select
            data-testid={IDS.askTradeSelect}
            className="industrial-input"
            value={trade}
            onChange={(e) => setTrade(e.target.value)}
          >
            {TRADES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        {/* Description */}
        <div>
          <label className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2 block">
            Describe the problem
          </label>
          <textarea
            data-testid={IDS.askTextarea}
            className="industrial-textarea"
            placeholder="e.g., My kitchen sink is dripping under the faucet. It started last night after I ran the dishwasher."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
          />
        </div>

        {/* Voice + Photo */}
        <div className="grid sm:grid-cols-2 gap-4">
          <button
            type="button"
            data-testid={IDS.askVoiceBtn}
            onClick={recording ? stopRecording : startRecording}
            disabled={transcribing}
            className={`flex items-center gap-3 px-4 py-4 border transition-colors ${
              recording
                ? 'border-red-500 bg-red-500/10 text-red-400 rec-pulse'
                : 'border-zinc-700 hover:border-amber-500 hover:text-amber-500 text-zinc-200'
            }`}
          >
            {transcribing ? (
              <Loader2 size={22} className="animate-spin" />
            ) : recording ? (
              <StopCircle size={22} />
            ) : (
              <Mic size={22} />
            )}
            <div className="text-left">
              <div className="font-mono uppercase text-xs tracking-widest">
                {recording ? 'Recording' : transcribing ? 'Processing' : 'Voice Input'}
              </div>
              <div className="text-xs text-zinc-500 font-mono">
                {recording ? 'Tap to stop' : 'Tap to describe by voice'}
              </div>
            </div>
          </button>

          <label
            className="flex items-center gap-3 px-4 py-4 border border-zinc-700 hover:border-amber-500 hover:text-amber-500 text-zinc-200 cursor-pointer transition-colors"
          >
            <Camera size={22} />
            <div className="text-left">
              <div className="font-mono uppercase text-xs tracking-widest">Add Photo</div>
              <div className="text-xs text-zinc-500 font-mono">PNG · JPG · WEBP</div>
            </div>
            <input
              data-testid={IDS.askPhotoInput}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={handlePhoto}
            />
          </label>
        </div>

        {voiceStatus && (
          <div data-testid={IDS.askVoiceStatus} className="font-mono text-xs uppercase tracking-widest text-amber-500">
            {voiceStatus}
          </div>
        )}

        {photoDataUrl && (
          <div className="relative inline-block border border-zinc-800 p-2">
            <img
              data-testid={IDS.askPhotoPreview}
              src={photoDataUrl}
              alt="Problem preview"
              className="max-h-48 object-contain"
            />
            <button
              data-testid={IDS.askPhotoRemove}
              onClick={removePhoto}
              className="absolute -top-3 -right-3 bg-zinc-900 border border-zinc-700 hover:border-red-500 hover:text-red-500 w-8 h-8 flex items-center justify-center"
              type="button"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {error && (
          <div
            data-testid={IDS.askError}
            className="border border-red-500/40 bg-red-500/10 text-red-400 px-3 py-2 text-sm font-mono flex items-center gap-2"
          >
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {/* Clarifying question from the buddy */}
        {clarifyQuestion && (
          <div
            data-testid="ask-clarify-panel"
            className="industrial-card !p-4 border-l-4 border-l-sky-400 bg-gradient-to-r from-sky-500/5 to-transparent"
          >
            <div className="flex items-start gap-3 mb-3">
              <CoreyAvatar size="sm" />
              <div className="flex-1">
                <div className="font-mono text-[10px] uppercase tracking-widest text-sky-400 mb-1 flex items-center gap-1">
                  <MessageCircleQuestion size={12} /> {guide.name} needs one thing
                </div>
                <div className="text-zinc-100 text-sm leading-relaxed" data-testid="ask-clarify-question">
                  {clarifyQuestion}
                </div>
              </div>
              <button
                type="button"
                onClick={replayClarifyQuestion}
                className="p-2 text-sky-400 hover:text-sky-300 border border-zinc-800 hover:border-sky-400"
                title="Replay question"
                data-testid="ask-clarify-replay"
              >
                <Volume2 size={14} />
              </button>
            </div>
            <textarea
              data-testid="ask-clarify-reply"
              className="industrial-textarea !min-h-[70px] text-sm"
              placeholder="Type your answer or hit the mic…"
              value={clarifyReply}
              onChange={(e) => setClarifyReply(e.target.value)}
              rows={2}
            />
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                type="button"
                onClick={replyRecording ? stopReplyRecording : startReplyRecording}
                disabled={replyTranscribing}
                data-testid="ask-clarify-mic"
                className={`inline-flex items-center gap-2 px-3 py-2 border font-mono text-xs uppercase tracking-widest transition-colors ${
                  replyRecording
                    ? 'border-red-500 bg-red-500/10 text-red-400 rec-pulse'
                    : 'border-zinc-700 hover:border-sky-400 hover:text-sky-400 text-zinc-200'
                }`}
              >
                {replyTranscribing ? <Loader2 size={14} className="animate-spin" /> : replyRecording ? <StopCircle size={14} /> : <Mic size={14} />}
                {replyRecording ? 'Stop' : 'Answer by voice'}
              </button>
              <button
                type="button"
                onClick={submitClarification}
                disabled={submitting || !clarifyReply.trim()}
                data-testid="ask-clarify-submit"
                className="btn-primary text-sm"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <>Send answer</>}
              </button>
              <button
                type="button"
                onClick={skipClarification}
                disabled={submitting}
                data-testid="ask-clarify-skip"
                className="text-zinc-400 hover:text-zinc-100 font-mono text-xs uppercase tracking-widest px-3 py-2"
              >
                Skip — just fix it
              </button>
            </div>
          </div>
        )}

        <button
          data-testid={IDS.askSubmit}
          onClick={handleSubmit}
          disabled={submitting || !!clarifyQuestion}
          className="btn-primary w-full text-base hover-wiggle"
        >
          {submitting ? (
            <><Loader2 size={18} className="animate-spin" /> AI is on it — hang tight…</>
          ) : clarifyQuestion ? (
            <>Waiting on your answer…</>
          ) : (
            <><Wrench size={18} /> Show Me How To Fix It</>
          )}
        </button>
      </div>
    </div>
  );
}
